import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip,
} from "recharts";
import { ArrowUpRight, ShieldCheck, TriangleAlert } from "lucide-react";

import { Card, CardLink, Metric, Pill, RangeSelect } from "@/components/dash/DashKit";
import { Loading } from "@/components/common/Chrome";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { COUNTRY_NAME, num } from "@/lib/format";

const relativeAgo = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso);
  const hours = (Date.now() - then.getTime()) / 3600000;
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} minutes ago`;
  if (hours < 24) return `${Math.round(hours)} hours ago`;
  const days = Math.round(hours / 24);
  return days < 30 ? `${days} day${days > 1 ? "s" : ""} ago` : `${Math.round(days / 30)} months ago`;
};

const FLAG = {
  IN: "🇮🇳", GB: "🇬🇧", US: "🇺🇸", SG: "🇸🇬", KE: "🇰🇪", AU: "🇦🇺", EU: "🇪🇺", GL: "🌐", CA: "🇨🇦",
};

const TABS = ["Overview", "Rules", "Obligations", "Assessments", "Incidents", "Regulatory Library"];

const LINKS = {
  Rules: "/regulatory/rules",
  Obligations: "/settings/organization",
  Assessments: "/regulatory/simulator",
  Incidents: "/platform/audit",
  "Regulatory Library": "/regulatory/evidence",
};

export default function ComplianceDashboard() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("6M");

  useEffect(() => {
    api.get(`/regulatory/intelligence?range=${range}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [range]);

  if (!data) return <Loading label="Assessing regulatory posture across jurisdictions" />;
  const k = data.kpis;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-[26px] font-bold tracking-tight text-foreground">Regulatory Compliance</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          Real-time compliance status across all jurisdictions
        </p>
      </div>

      <Tabs defaultValue="Overview">
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`reg-tab-${t.toLowerCase().replace(/ /g, "-")}`}
                         onClick={() => { if (LINKS[t]) window.location.assign(LINKS[t]); }}
                         className="rounded-none border-b-2 border-transparent px-3 pb-2.5 pt-1 text-[13px] font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric testId="kpi-overall-compliance" tone="emerald" icon={ShieldCheck} label="Overall Compliance"
                value={`${k.overall_compliance}%`} footer={k.overall_compliance >= 90 ? "Excellent" : "Improving"} />
        <Metric testId="kpi-jurisdictions" tone="indigo" label="Jurisdictions Covered"
                value={num(k.jurisdictions_covered)} footer="Active" />
        <Metric testId="kpi-active-rules" tone="teal" label="Active Rules"
                value={num(k.active_rules)} footer={`${k.published_rules} published`} />
        <Metric testId="kpi-pending-actions" tone="amber" icon={TriangleAlert} label="Pending Actions"
                value={num(k.pending_actions)} footer="Requires attention" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card testId="panel-compliance-by-category" title="Compliance by Category"
              action={<RangeSelect value={range} onChange={setRange} testId="category-range" />}>
          <div className="text-[10.5px] text-muted-foreground">
            Scored across {data.window_rules} rule versions effective in the selected window
          </div>
          <div className="h-[228px] w-full">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <RadarChart data={data.by_category} outerRadius="72%">
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar name="Your Score" dataKey="score" stroke="hsl(var(--chart-1))"
                       fill="hsl(var(--chart-1))" fillOpacity={0.22} strokeWidth={2} />
                <Radar name="Global Benchmark" dataKey="benchmark" stroke="hsl(var(--teal))"
                       strokeDasharray="4 4" fill="none" strokeWidth={1.6} />
                <Tooltip contentStyle={{
                  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
                  borderRadius: 10, fontSize: 12,
                }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 text-[11.5px]">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="h-0.5 w-5 rounded bg-[hsl(var(--chart-1))]" /> Your Score
            </span>
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <span className="h-0.5 w-5 rounded border-t-2 border-dashed border-[hsl(var(--teal))]" /> Global Benchmark
            </span>
          </div>
        </Card>

        <Card testId="panel-regulatory-updates" title="Top Regulatory Updates"
              action={<Link to="/regulatory/rules"><CardLink testId="view-all-updates">View all updates</CardLink></Link>}>
          <div className="space-y-1.5">
            {data.updates.map((u, i) => (
              <div key={`${u.title}-${i}`} className="flex items-start gap-3 rounded-lg px-1 py-2.5 transition-colors duration-150 hover:bg-muted/60"
                   data-testid={`regulatory-update-${i}`}>
                <span className="mt-0.5 text-[15px] leading-none">{FLAG[u.country] || "🌐"}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-semibold text-foreground">{u.regulator}</div>
                  <div className="truncate text-[11.5px] text-muted-foreground">{u.title}</div>
                </div>
                <span className="shrink-0 text-[11px] text-muted-foreground">{relativeAgo(u.ago)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card testId="panel-jurisdiction-compliance" title="Jurisdiction Compliance" padded={false}
            action={<Link to="/settings/organization"><CardLink testId="view-all-jurisdictions">View all jurisdictions</CardLink></Link>}>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse" data-testid="jurisdiction-table">
            <thead>
              <tr className="border-y border-border bg-muted/40">
                {["Jurisdiction", "Regulator", "Compliance", "Risk", "Open items", ""].map((h) => (
                  <th key={h} className="whitespace-nowrap px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.jurisdictions.map((j) => (
                <tr key={j.country} className="row-hover" data-testid={`jurisdiction-row-${j.country}`}>
                  <td className="whitespace-nowrap px-5 py-3">
                    <span className="mr-2 text-[14px]">{FLAG[j.country] || "🌐"}</span>
                    <span className="text-[12.5px] font-medium text-foreground">
                      {COUNTRY_NAME[j.country] || j.country}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-muted-foreground">{j.regulator}</td>
                  <td className="whitespace-nowrap px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[12.5px] font-semibold text-foreground">{j.compliance}%</span>
                      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(var(--teal))]"
                             style={{ width: `${j.compliance}%` }} />
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3">
                    <Pill tone={j.risk} testId={`jurisdiction-risk-${j.country}`}>{j.risk}</Pill>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-[12.5px] text-foreground">{j.open_items}</td>
                  <td className="whitespace-nowrap px-5 py-3 text-right">
                    <Link to="/regulatory/rules"
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:opacity-75"
                          data-testid={`jurisdiction-view-${j.country}`}>
                      View <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
